/**
 * Language switch (snippets/olin-language-menu.liquid): the button shows the current language and
 * opens the list of the other languages under it.
 * - A mouse opens it on hover, with short delays so it doesn't flicker when the pointer only passes by;
 *   a click with the mouse keeps it open.
 * - Touch and keyboard toggle it with the button.
 * - Escape, a click outside and moving focus out close it.
 */
const OPEN_DELAY = 120;
const CLOSE_DELAY = 250;

class OlinLanguageMenu extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {number | undefined} */
  #timer;
  #pointerType = '';

  connectedCallback() {
    this.#button = this.querySelector('.olin-lang__toggle');
    if (!this.#button) return;

    this.#button.addEventListener('pointerdown', this.#onPointerDown);
    this.#button.addEventListener('click', this.#onClick);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusout', this.#onFocusOut);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  get open() {
    return this.#button?.getAttribute('aria-expanded') === 'true';
  }

  set open(value) {
    clearTimeout(this.#timer);
    if (!this.#button || value === this.open) return;

    this.#button.setAttribute('aria-expanded', String(value));
    if (value) {
      document.addEventListener('click', this.#onDocumentClick);
    } else {
      document.removeEventListener('click', this.#onDocumentClick);
    }
  }

  /**
   * @param {boolean} value
   * @param {number} delay
   */
  #setLater(value, delay) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.open = value;
    }, delay);
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerType = event.pointerType;
  };

  #onClick = () => {
    // The mouse already opened the list by hovering, so its click must not close it again.
    // A click without a pointer (keyboard) or from touch toggles it.
    if (this.#pointerType === 'mouse') {
      this.open = true;
    } else {
      this.open = !this.open;
    }
    this.#pointerType = '';
  };

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType === 'mouse') this.#setLater(true, OPEN_DELAY);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType === 'mouse') this.#setLater(false, CLOSE_DELAY);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.open) return;

    this.open = false;
    this.#button?.focus();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.open = false;
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.open = false;
  };
}

if (!customElements.get('olin-language-menu')) {
  customElements.define('olin-language-menu', OlinLanguageMenu);
}
